import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Tldraw,
  DefaultStylePanel,
  Editor,
  type TLPresenceUserInfo,
  type TLStore,
} from "tldraw";
import { useSync } from "@tldraw/sync";
import { useAuthStore } from "@/store/useAuthStore";
import { isOrgAdminWithoutActingTeacher } from "@/utils/role-utils";
import { useSpaceStore } from "../store/useSpaceStore";
import { useViewport } from "@/hooks/useViewport";
import { isInSyncRemove } from "../spaces-diff-filter";
import SpaceTopBar from "../components/SpaceTopBar";
import { EditSpaceModal } from "../components/EditSpaceModal";
import ParticipantPanel from "../components/ParticipantPanel";
import { ClosedCanvas, toEditorSnapshot } from "../components/ClosedCanvas";
import {
  MuxSpaceCanvas,
  StudentWorkCanvas,
} from "../components/teacherSpaceCanvasComponents";
import { useTeacherSpaceSession } from "../hooks/useTeacherSpaceSession";
import { useLessonImport } from "../hooks/useLessonImport";
import { useTldrawDrawLock } from "@/drawlock/useTldrawDrawLock";
import { TldrawDrawToolRegion } from "@/drawlock/TldrawDrawToolRegion";
import { SPACES_TEACHER_SURFACE_ID } from "@/drawlock/surfaceIds";
import {
  CANVAS_COMPONENTS,
  CANVAS_OPTIONS,
  CANVAS_UI_OVERRIDES,
} from "@/canvas/canvasOptions";
import { useConnectionStore } from "@/features/tutor/store/useConnectionStore";
import SystemToasts from "@/features/tutor/components/connection/SystemToasts";
import { useSpace } from "../hooks/useSpace";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useTeacherDraftSnapshot } from "../hooks/useTeacherDraftSnapshot";
import { SLIDE_EMBED_TYPE } from "../shapes/SlideEmbedShapeUtil";
import { SPACES_SHAPE_UTILS, SPACES_COMPONENTS_BASE } from "../constants";
import { editorSnapshotToRoomSnapshot } from "../spacesCanvasUtils";
import { getAvatarColor } from "../spacesPresenceUtils";
import { CanvasStylePanel } from "@/canvas/components/CanvasPanels";
import { useSyncMultiplexer } from "../hooks/useSyncMultiplexer";
import { SHARED_LAYER } from "../constants";
import "@/canvas/canvas.css";
import SpacesOrgAdminBlocker from "../components/SpacesOrgAdminBlocker";
import { TLDRAW_LICENSE_KEY } from "@/constants/tldraw";
import { ET_CANVAS_ASSET_URLS } from "@ever-tutor/et-ui";

const _onLoadingMount = (editor: Editor) => {
  editor.setCameraOptions({ zoomSteps: [1] });
  editor.updateInstanceState({ isGridMode: true });
};

export default function TeacherSpacePage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const storeSpaceId = useSpaceStore((s) => s.spaceId);
  const startedAt = useSpaceStore((s) => s.startedAt);
  const { isMobile, isMobilePortrait, isMobileLandscape, isTabletPortrait } =
    useViewport();

  const draftComponents = useMemo(
    () => ({
      ...SPACES_COMPONENTS_BASE,
      StylePanel:
        isMobilePortrait || isMobileLandscape || isTabletPortrait
          ? DefaultStylePanel
          : CanvasStylePanel,
    }),
    [isMobilePortrait, isMobileLandscape, isTabletPortrait],
  );

  // Sync URL spaceId into store on mount (handles page refresh)
  useEffect(() => {
    if (spaceId && storeSpaceId !== spaceId) {
      useSpaceStore.getState().setSpace(spaceId, "", null);
    }
  }, [spaceId, storeSpaceId]);

  const editorRef = useRef<Editor | null>(null);
  const getEditor = useCallback(() => editorRef.current, []);

  const {
    spaceInfo,
    spaceStatus,
    setSpaceStatus,
    teacherLastSaved,
    setTeacherLastSaved,
    studentCameras,
  } = useTeacherSpaceSession({ spaceId, token });
  const isSpaceClosed = spaceStatus === "closed";
  const isSpaceDraft = spaceStatus === "draft";

  // Space bar locks the pen down for trackpad handwriting, same as the student
  // canvas. A closed space renders ClosedCanvas instead of an editor, so the
  // lock stands down there; draft and paused both stay writable, because
  // pausing only stops students — the teacher keeps authoring.
  useTldrawDrawLock({
    surfaceId: SPACES_TEACHER_SURFACE_ID,
    getEditor,
    isActive: !isSpaceClosed,
  });
  const [participantPanelOpen, setParticipantPanelOpen] = useState(!isMobile);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { saveSnapshot, pauseSpace, resumeSpace, publishSpace } = useSpace();
  const { showNotification } = useNotificationStore();

  // Draft mode: local editor ref (no DO sync)
  const draftEditorRef = useRef<Editor | null>(null);

  // When acting as a teacher, all canvas identity (cursor, shape stamps, presence)
  // must use the teacher's user_id — not the org admin's. spaceInfo.userId is the
  // effective user_id returned by the join endpoint.
  const effectiveUserId = spaceInfo?.userId ?? user?.id;

  const userInfo = useMemo<TLPresenceUserInfo | undefined>(() => {
    if (!user || !effectiveUserId) return undefined;
    const effectiveName =
      (actingTeacherId ? spaceInfo?.teacherName : null) ||
      user.name ||
      user.username ||
      "";
    return {
      id: String(effectiveUserId),
      name: effectiveName,
      color: getAvatarColor(effectiveUserId).text,
    };
  }, [user, effectiveUserId, actingTeacherId, spaceInfo?.teacherName]);

  const getDraftSnapshot = useCallback((): Record<string, unknown> | null => {
    const editor = draftEditorRef.current;
    if (!editor) return null;
    return editorSnapshotToRoomSnapshot(editor);
  }, []);
  const {
    save: handleDraftSave,
    lastSaved: draftLastSaved,
    markSaved: markDraftSaved,
  } = useTeacherDraftSnapshot(spaceId, isSpaceDraft, getDraftSnapshot, null);

  // In draft mode there's no DO sync, so a successful local-only import is the
  // moment we should pin into the draft autosave loop. In live mode the
  // autosave is sync-driven and onImported stays a no-op.
  const onLessonImported = isSpaceDraft ? markDraftSaved : undefined;

  const { tryImport, markSynced, resetSynced, markImportDone } =
    useLessonImport({
      spaceId,
      userId: user?.id,
      lessonIds: spaceInfo?.lessonIds,
      isAlreadyImported: !!spaceInfo?.lessonImported,
      getEditor,
      onImported: onLessonImported,
    });

  const onWhiteboardMount = useCallback(
    (editor: Editor) => {
      editor.setCameraOptions({ zoomSteps: [1] });
      editor.updateInstanceState({ isGridMode: true });
      // Pen in hand from the start, as on the notepad.
      editor.setCurrentTool("draw");
      editorRef.current = editor;
      const stampUserId = effectiveUserId ?? user?.id;
      if (!stampUserId) return;
      editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
        if (isInSyncRemove()) return;
        if ((shape as any).type === SLIDE_EMBED_TYPE) return false;
      });
      editor.sideEffects.registerBeforeCreateHandler(
        "shape",
        (shape, source) => {
          if (source !== "user") return shape;
          return {
            ...shape,
            meta: {
              ...(shape.meta as object),
              role: "teacher",
              createdBy: stampUserId,
              layer: SHARED_LAYER,
            },
          };
        },
      );
      tryImport();
    },
    [effectiveUserId, user?.id, tryImport],
  );

  const onSynced = useCallback(() => {
    markSynced();
    tryImport();
  }, [markSynced, tryImport]);

  const handleTeacherSave = useCallback(async () => {
    if (!spaceId) return;
    await saveSnapshot(spaceId);
    setTeacherLastSaved(new Date());
  }, [spaceId, saveSnapshot]);

  const handlePublish = useCallback(async () => {
    if (!spaceId) return;
    await handleDraftSave();
    await publishSpace(spaceId);
    navigate(0);
  }, [spaceId, handleDraftSave, publishSpace, navigate]);

  const handlePause = useCallback(async () => {
    if (!spaceId) return;
    await pauseSpace(spaceId);
    setSpaceStatus("paused");
    showNotification(
      "Space paused — students can't draw or interact.",
      NotificationType.SUCCESS,
    );
  }, [spaceId, pauseSpace, showNotification]);

  const handleResume = useCallback(async () => {
    if (!spaceId) return;
    await resumeSpace(spaceId);
    setSpaceStatus("open");
    showNotification(
      "Space resumed — students can interact again.",
      NotificationType.SUCCESS,
    );
  }, [spaceId, resumeSpace, showNotification]);

  // Draft mode local canvas mount — mirrors onWhiteboardMount but without DO side effects
  const onDraftMount = useCallback(
    (editor: Editor) => {
      editor.setCameraOptions({ zoomSteps: [1] });
      editor.updateInstanceState({ isGridMode: true });
      editor.setCurrentTool("draw");
      draftEditorRef.current = editor;
      editorRef.current = editor;
      // Draft canvas has no sync layer — pretend it's synced so tryImport can
      // run the local-only slide import path.
      markSynced();
      if (spaceInfo?.initialSnapshot) {
        try {
          editor.loadSnapshot(toEditorSnapshot(spaceInfo.initialSnapshot));
        } catch (err) {
          console.error("[TeacherSpacePage] malformed draft snapshot:", err);
        }
      }
      const stampUserId = effectiveUserId ?? user?.id;
      if (!stampUserId) return;
      editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
        if ((shape as any).type === SLIDE_EMBED_TYPE) return false;
      });
      editor.sideEffects.registerBeforeCreateHandler(
        "shape",
        (shape, source) => {
          if (source !== "user") return shape;
          return {
            ...shape,
            meta: {
              ...(shape.meta as object),
              role: "teacher",
              createdBy: stampUserId,
              layer: SHARED_LAYER,
            },
          };
        },
      );
      tryImport();
    },
    [
      effectiveUserId,
      user?.id,
      spaceInfo?.initialSnapshot,
      tryImport,
      markSynced,
    ],
  );

  const spacesBase = import.meta.env.VITE_SPACES_BASE_URL as string;
  const isActive = spaceStatus === "open" || spaceStatus === "paused";
  const syncUri =
    spaceId && token && isActive
      ? `${spacesBase.replace(/^http/, "ws")}/spaces/sync/${spaceId}?token=${token}${actingTeacherId ? `&actAsTeacherId=${actingTeacherId}` : ""}`
      : null;

  const mux = useSyncMultiplexer(syncUri);

  // Reflect mux WS state into the global connection store so SystemToasts
  // can show "unstable" / "back online" banners on transient sync drops.
  useEffect(() => {
    if (!mux) return;
    return mux.onConnectionChange((isConnected) => {
      const store = useConnectionStore.getState();
      if (isConnected) store.markConnected();
      else store.markDisconnected();
    });
  }, [mux]);

  // Mux generation — bumped on each unexpected disconnect. Used as a React `key`
  // on canvas + bridge so useSync fully remounts after a reconnect, getting a
  // fresh tldraw client + clean handshake (otherwise a DO restart would leave
  // the client trying to resume a session the server no longer knows about,
  // surfacing as tldraw's "Something went wrong" error).
  const [muxGeneration, setMuxGeneration] = useState(0);
  useEffect(() => {
    if (!mux) return;
    return mux.onGenerationChange((gen) => {
      setMuxGeneration(gen);
      // Drop refs to the old session so a fresh sync cycle starts cleanly.
      // sharedStore is set again by MuxSpaceCanvas.onStoreReady once the new
      // useSync reaches synced-remote.
      if (gen > 0) {
        setSharedStore(null);
        resetSynced();
        markImportDone(); // never re-import slides on reconnect
      }
    });
  }, [mux]);

  // Student Work view
  const [watchedStudentId, setWatchedStudentId] = useState<string | null>(null);
  // Resolve watched student's display name from the participants list for the
  // SpaceTopBar watching chip. Falls back to userId so the chip never shows blank.
  const participants = useSpaceStore((s) => s.participants);
  const watchedParticipant = watchedStudentId
    ? participants.find((p) => String(p.userId) === watchedStudentId)
    : null;
  const watchedStudentName = watchedStudentId
    ? (watchedParticipant?.name ?? watchedStudentId)
    : null;

  // Shared store from MuxSpaceCanvas — published as state once `synced-remote` so
  // StudentWorkCanvas can re-render when shared becomes available after personal.
  const [sharedStore, setSharedStore] = useState<TLStore | null>(null);
  const onStoreReady = useCallback((s: ReturnType<typeof useSync>) => {
    if (s.status === "synced-remote" && s.store) {
      setSharedStore((prev) => (prev === s.store ? prev : s.store));
    }
  }, []);

  // While the teacher is watching a student, explicitly remove their own
  // instance_presence record from the shared room. useSync's getUserPresence
  // returning null suppresses *new* publishes but doesn't retroactively delete
  // the existing record — without this remove, students see the teacher's last
  // cursor frozen on the shared canvas.
  useEffect(() => {
    if (!watchedStudentId || !sharedStore) return;
    const teacherId =
      effectiveUserId !== undefined ? String(effectiveUserId) : null;
    if (!teacherId) return;
    const stale = sharedStore
      .allRecords()
      .filter(
        (r: any) =>
          r?.typeName === "instance_presence" && r?.userId === teacherId,
      );
    if (stale.length === 0) return;
    sharedStore.remove(stale.map((r: any) => r.id));
  }, [watchedStudentId, sharedStore, effectiveUserId]);

  const studentWorkComponents = useMemo(
    () => ({
      ...SPACES_COMPONENTS_BASE,
      StylePanel:
        isMobilePortrait || isMobileLandscape || isTabletPortrait
          ? DefaultStylePanel
          : CanvasStylePanel,
    }),
    [isMobilePortrait, isMobileLandscape, isTabletPortrait],
  );

  if (user && isOrgAdminWithoutActingTeacher(user.role, actingTeacherId)) {
    return <SpacesOrgAdminBlocker fullScreen />;
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-white font-mulish">
      <SystemToasts />
      <SpaceTopBar
        spaceStatus={spaceStatus}
        onSave={isSpaceDraft ? undefined : handleTeacherSave}
        lastSaved={isSpaceDraft ? draftLastSaved : teacherLastSaved}
        participantPanelOpen={participantPanelOpen}
        onToggleParticipantPanel={
          isSpaceDraft ? undefined : () => setParticipantPanelOpen((o) => !o)
        }
        onEdit={
          spaceStatus === "open" || spaceStatus === "paused"
            ? () => setEditModalOpen(true)
            : undefined
        }
        onPause={handlePause}
        onResume={handleResume}
        onPublish={handlePublish}
        onSaveDraft={handleDraftSave}
        startedAt={startedAt}
        watchingStudentId={watchedParticipant?.userId ?? null}
        watchingStudentName={watchedStudentName}
        onStopWatching={
          watchedStudentId ? () => setWatchedStudentId(null) : undefined
        }
      />
      {editModalOpen && spaceId && (
        <EditSpaceModal
          spaceId={spaceId}
          onClose={() => setEditModalOpen(false)}
          onSaved={(updated) => {
            useSpaceStore
              .getState()
              .setSpace(spaceId, updated.spaceName, updated.lessonContext);
            setEditModalOpen(false);
          }}
        />
      )}

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 relative">
          {isSpaceClosed ? (
            <ClosedCanvas snapshot={spaceInfo?.initialSnapshot} />
          ) : isSpaceDraft ? (
            <div className="tldraw-canvas">
              <Tldraw
                licenseKey={TLDRAW_LICENSE_KEY}
                assetUrls={ET_CANVAS_ASSET_URLS}
                key="draft"
                shapeUtils={SPACES_SHAPE_UTILS}
                options={CANVAS_OPTIONS}
                overrides={CANVAS_UI_OVERRIDES}
                components={draftComponents}
                onMount={onDraftMount}
              >
                <TldrawDrawToolRegion />
              </Tldraw>
            </div>
          ) : mux ? (
            <>
              {/* Teacher whiteboard — hidden (not unmounted) when watching a student.
                  Keyed by generation so a DO restart fully remounts useSync.
                  userInfo is dropped while watching a student so the teacher does
                  NOT publish a cursor into the shared room — otherwise students
                  would see a phantom teacher cursor moving over their slides
                  while the teacher is actually drawing on a student's personal canvas. */}
              <div
                style={{ display: watchedStudentId ? "none" : "block" }}
                className="absolute inset-0"
              >
                <MuxSpaceCanvas
                  key={`shared-gen${muxGeneration}`}
                  mux={mux}
                  userInfo={userInfo}
                  shouldPublishPresence={!watchedStudentId}
                  onMount={onWhiteboardMount}
                  onSynced={onSynced}
                  onStoreReady={onStoreReady}
                  stylePanel={
                    isMobilePortrait || isMobileLandscape || isTabletPortrait
                      ? DefaultStylePanel
                      : CanvasStylePanel
                  }
                />
              </div>
              {/* Student Work view — keyed so the channel reconnects on switch
                  AND on every mux generation bump.
                  Wrapped in `absolute inset-0` to match the shared canvas: gives
                  the editor a fixed-size container so wheel/pan stay inside the
                  canvas (instead of bubbling to page scroll) and tldraw's
                  getBoundingClientRect projects user cursors at the right offset. */}
              {watchedStudentId && user?.id !== undefined && (
                <div className="absolute inset-0">
                  <StudentWorkCanvas
                    key={`${watchedStudentId}-gen${muxGeneration}`}
                    mux={mux}
                    watchedStudentId={watchedStudentId}
                    teacherUserId={effectiveUserId ?? user.id}
                    teacherUserInfo={userInfo}
                    sharedStore={sharedStore}
                    components={studentWorkComponents}
                    initialCamera={studentCameras.get(watchedStudentId) ?? null}
                    liveCamera={studentCameras.get(watchedStudentId) ?? null}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="tldraw-canvas">
              <Tldraw
                licenseKey={TLDRAW_LICENSE_KEY}
                assetUrls={ET_CANVAS_ASSET_URLS}
                key="loading"
                components={CANVAS_COMPONENTS}
                shapeUtils={SPACES_SHAPE_UTILS}
                options={CANVAS_OPTIONS}
                overrides={CANVAS_UI_OVERRIDES}
                onMount={_onLoadingMount}
              />
            </div>
          )}
        </div>

        {(spaceStatus === "open" || spaceStatus === "paused") &&
          participantPanelOpen && (
            <ParticipantPanel
              watchedStudentId={watchedStudentId}
              onWatchStudent={setWatchedStudentId}
            />
          )}
      </div>
    </div>
  );
}
